import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { post } from "@/lib/api";
import { allClientsQuery, clientLabel, pickableClients } from "@/lib/clients";
import type { Document } from "@/lib/types";

export function NewDocumentDialog({ type, open, onClose }: { type: "invoice" | "offer"; open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const clients = useQuery(allClientsQuery);
  const [clientId, setClientId] = useState("");
  const noun = type === "offer" ? "Angebot" : "Rechnung";

  const create = useMutation({
    mutationFn: () => post<{ document: Document; warning: string | null }>("/api/documents", { type, clientId: Number(clientId) }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["documents"] });
      if (r.warning) toast.warning(r.warning);
      onClose();
      navigate(`/rechnungen/${r.document.id}`);
    },
    onError: (e) => toast.error(e.message),
  });

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>Neues {noun}</DialogTitle></DialogHeader>
        <form className="contents" onSubmit={(e) => { e.preventDefault(); if (clientId && !create.isPending) create.mutate(); }}>
          <div className="space-y-1.5">
            <Label htmlFor="new-doc-client">Kunde</Label>
            <Select value={clientId} onValueChange={setClientId}>
              <SelectTrigger id="new-doc-client" className="w-full pointer-coarse:h-11"><SelectValue placeholder="Kunde wählen" /></SelectTrigger>
              <SelectContent>
                {pickableClients(clients.data?.clients ?? []).map((c) => <SelectItem key={c.id} value={String(c.id)}>{clientLabel(c)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" className="pointer-coarse:h-11" onClick={onClose}>Abbrechen</Button>
            <Button type="submit" className="pointer-coarse:h-11" disabled={!clientId || create.isPending}>Anlegen</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
